import React from "react";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtUsd } from "@/lib/format";
import api from "@/lib/api";
import { Sparkles, Gift, Network, Trophy, Zap, Coins, Award, Wallet, ArrowDownLeft, ArrowUpRight } from "lucide-react";

// kind: "in" = new income (counts toward total), "out" = money leaving (withdrawals),
// "neutral" = capital movement that is not new income (claim realize / principal return).
const META = {
  roi_daily:        { label: "Daily ROI", Icon: Coins, kind: "in" },
  direct_bonus:     { label: "Direct sponsor bonus", Icon: Gift, kind: "in" },
  level_income:     { label: "Override income", Icon: Network, kind: "in" },
  rank_commission:  { label: "Rank commission", Icon: Trophy, kind: "in" },
  same_rank_bonus:  { label: "Same-rank bonus", Icon: Award, kind: "in" },
  rank_reward:      { label: "Rank reward", Icon: Trophy, kind: "in" },
  prediction_bonus: { label: "Prediction bonus", Icon: Sparkles, kind: "in" },
  croc_pool:        { label: "Croc Pool", Icon: Trophy, kind: "in" },
  marketing_point:  { label: "Marketing points", Icon: Zap, kind: "in" },
  principal_return: { label: "Principal returned", Icon: ArrowDownLeft, kind: "neutral" },
  claim:            { label: "Claim (to balance)", Icon: ArrowDownLeft, kind: "neutral" },
  withdrawal:       { label: "Withdrawal", Icon: ArrowUpRight, kind: "out" },
  mkt_withdraw:     { label: "Marketing withdrawal", Icon: ArrowUpRight, kind: "out" },
};

const ago = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export const RewardsHistory = ({ limit = 12, title = "Your Rewards" }) => {
  const { address } = useWallet();
  const { data } = usePolling(() => api.userRewards(address || "0x0"), [address], 20000);
  const rows = (data?.rewards ?? []).slice(0, limit);
  // income total counts only real earnings, not withdrawals/claims/principal movement
  const incomeTotal = (data?.rewards ?? [])
    .filter((r) => (META[r.type]?.kind ?? "in") === "in")
    .reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <Panel data-testid="rewards-history">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 gold-text" />
          <span className="font-display font-bold text-white">{title}</span>
        </div>
        <Pill tone="emerald" data-testid="rewards-total">{fmtUsd(incomeTotal)} earned</Pill>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm" data-testid="rewards-empty">
          No rewards yet — stake, refer and predict daily to start earning.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
          {rows.map((r, i) => {
            const m = META[r.type] || { label: (r.type || "reward").replace(/_/g, " "), Icon: Coins, kind: "in" };
            const out = m.kind === "out";
            const neutral = m.kind === "neutral";
            const amtCls = out ? "text-red-400" : neutral ? "text-muted-foreground" : "emerald-text";
            const sign = out ? "−" : neutral ? "" : "+";
            return (
              <div key={r.id || i} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2.5" data-testid={`reward-row-${i}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 border ${out ? "bg-red-500/10 border-red-500/25" : "bg-[#1CFD4C]/10 border-[#1CFD4C]/25"}`}>
                    <m.Icon className={`w-3.5 h-3.5 ${out ? "text-red-400" : "emerald-text"}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{m.label}</div>
                    <div className="text-[10px] text-muted-foreground">{ago(r.created_at)}</div>
                  </div>
                </div>
                <span className={`font-num text-sm shrink-0 ${amtCls}`} data-testid={`reward-amount-${i}`}>{sign}{fmtUsd(r.amount)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};
